import posthog from "posthog-js";
import {PostHogProvider} from "posthog-js/react";
import {useEffect} from "react";
import {analytics} from "../services/analytics";
import {setupGlobalErrorHandlers} from "../utils/error-handlers";
import {isElectron} from "../utils/env";
import {db} from "@zeg/instant/db";

// PostHog configuration - centralized here
const POSTHOG_CONFIG = {
  apiKey: "phc_8s5nDGblHxoY2nXhmyns1Oj7aFHccvbGzSNCtmTFgTA",
  apiHost: "https://us.i.posthog.com",
  defaults: "2025-05-24",
  personProfiles: "identified_only" as const,
};

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

// Component to handle analytics tracking
function AnalyticsTracking() {
  const {user} = db.useAuth();
  const platform = isElectron() ? "desktop" : "web";

  // Track user identification and session
  useEffect(() => {
    if (user?.id && user?.email) {
      analytics.identify(user.id, {email: user.email, platform});

      analytics.track("user_session_started", {
        platform,
        app_name: platform === "desktop" ? "Zeg Desktop" : "Zeg Web",
      });
    }
  }, [user?.id, user?.email, platform]);

  return null;
}

export function AnalyticsProvider({children}: AnalyticsProviderProps) {
  const platform = isElectron() ? "desktop" : "web";
  const appVersion =
    (typeof import.meta !== "undefined" &&
      (import.meta as any).env?.VITE_APP_VERSION) ||
    "1.0.0";

  useEffect(() => {
    analytics.init({
      apiKey: POSTHOG_CONFIG.apiKey,
      apiHost: POSTHOG_CONFIG.apiHost,
      isDevelopment:
        (typeof import.meta !== "undefined" && (import.meta as any).env?.DEV) ||
        false,
      platform,
      appVersion,
      defaults: POSTHOG_CONFIG.defaults,
      personProfiles: POSTHOG_CONFIG.personProfiles,
    });

    // Track initial app load
    analytics.track("app_loaded", {
      app_name: platform === "desktop" ? "Zeg Desktop" : "Zeg Web",
      platform,
      referrer: document.referrer,
      screen_size: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    });

    // Set up global error handlers
    setupGlobalErrorHandlers();
  }, []); // Only run once on mount

  return (
    <PostHogProvider client={posthog}>
      <AnalyticsTracking />
      {children}
    </PostHogProvider>
  );
}
